import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Toolbar,
    Typography,
} from '@mui/material';
import 'cropperjs/dist/cropper.css';
import { createRef, useEffect, useState } from 'react';
import { Cropper, ReactCropperElement } from 'react-cropper';

export type ReceiptCropItem = {
    file: File;
    imageUrl: string;
};

interface ReceiptImageCropDialogProps {
    open: boolean;
    items: ReceiptCropItem[];
    onClose: () => void;
    onCropConfirm: (croppedFiles: File[]) => void;
}

async function canvasToFile(
    canvas: HTMLCanvasElement,
    file: File
): Promise<File> {
    const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            result => {
                if (result) resolve(result);
                else reject(new Error('Could not crop image'));
            },
            file.type || 'image/jpeg',
            0.95
        );
    });
    return new File([blob], file.name, {
        type: file.type || 'image/jpeg',
        lastModified: Date.now(),
    });
}

export const ReceiptImageCropDialog = ({
    open,
    items,
    onClose,
    onCropConfirm,
}: ReceiptImageCropDialogProps) => {
    const cropperRef = createRef<ReactCropperElement>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    // Crops saved when switching thumbnails; null = still use live cropper / original
    const [savedCrops, setSavedCrops] = useState<(File | null)[]>([]);

    useEffect(() => {
        if (!open) return;
        setActiveIndex(0);
        setSavedCrops(items.map(() => null));
        setIsProcessing(false);
    }, [open, items]);

    const activeItem = items[activeIndex] ?? null;

    const readCurrentCrop = async (): Promise<File | null> => {
        const cropper = cropperRef.current?.cropper;
        if (!cropper || !activeItem) return null;
        try {
            const canvas = cropper.getCroppedCanvas();
            if (!canvas) return null;
            return await canvasToFile(canvas, activeItem.file);
        } catch {
            return null;
        }
    };

    const switchTo = async (index: number) => {
        if (index === activeIndex || isProcessing || !items[index]) return;
        setIsProcessing(true);
        try {
            const cropped = await readCurrentCrop();
            if (cropped) {
                setSavedCrops(prev => {
                    const next = [...prev];
                    next[activeIndex] = cropped;
                    return next;
                });
            }
            setActiveIndex(index);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCropConfirm = async () => {
        if (!items.length) return;
        setIsProcessing(true);
        try {
            const cropped = await readCurrentCrop();
            const results = items.map((item, index) => {
                if (index === activeIndex && cropped) return cropped;
                return savedCrops[index] ?? item.file;
            });
            onCropConfirm(results);
            onClose();
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    height: { xs: '100vh', md: 'auto' },
                    maxHeight: { xs: '100vh', md: '90vh' },
                    margin: { xs: 0, md: 'auto' },
                    borderRadius: { xs: 0, md: 1 },
                },
            }}
        >
            <DialogTitle>
                {items.length > 1
                    ? `Crop receipt images (${activeIndex + 1}/${items.length})`
                    : 'Crop receipt image'}
            </DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'auto',
                    py: 2,
                }}
            >
                {activeItem && (
                    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                        <Cropper
                            key={`${activeIndex}-${activeItem.imageUrl}`}
                            ref={cropperRef}
                            src={activeItem.imageUrl}
                            guides={true}
                            cropBoxResizable={true}
                            autoCropArea={1}
                            responsive={true}
                            restore={true}
                            center={true}
                            highlight={true}
                            dragMode="crop"
                            background={true}
                            modal={true}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '55vh',
                            }}
                        />
                        {items.length > 1 && (
                            <Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 1 }}
                                >
                                    Tap a thumbnail to crop that page
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{ justifyContent: 'center' }}
                                >
                                    {items.map((item, index) => {
                                        const isActive = index === activeIndex;
                                        return (
                                            <Box
                                                key={`${item.file.name}-${index}`}
                                                component="button"
                                                type="button"
                                                onClick={() => void switchTo(index)}
                                                disabled={isProcessing}
                                                aria-label={`Crop ${item.file.name}`}
                                                aria-pressed={isActive}
                                                sx={{
                                                    p: 0,
                                                    border: 2,
                                                    borderColor: isActive
                                                        ? 'primary.main'
                                                        : 'transparent',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    cursor: isProcessing
                                                        ? 'wait'
                                                        : 'pointer',
                                                    bgcolor: 'action.hover',
                                                    opacity: isActive ? 1 : 0.4,
                                                    transition: 'opacity 0.15s',
                                                    '&:hover': {
                                                        opacity: isActive
                                                            ? 1
                                                            : 0.7,
                                                    },
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={item.imageUrl}
                                                    alt={item.file.name}
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 0 }}>
                <Toolbar
                    sx={{ width: '100%', justifyContent: 'space-between' }}
                >
                    <Button
                        variant="contained"
                        onClick={() => void handleCropConfirm()}
                        disabled={isProcessing || !items.length}
                    >
                        {items.length > 1 ? 'Confirm all crops' : 'Confirm crop'}
                    </Button>
                    <Button
                        variant="text"
                        onClick={handleCancel}
                        disabled={isProcessing}
                    >
                        Cancel
                    </Button>
                </Toolbar>
            </DialogActions>
        </Dialog>
    );
};
